/**
 * Image Detector Module
 *
 * Responsible for finding and tracking images in the DOM that should be animated.
 * Handles CSS selector matching, data attribute parsing, and MutationObserver
 * for dynamically added images.
 */

import {
    AnimationConfig,
    AnimationIntensity,
    AnimationMode,
    AnimationType,
    TrackedImage,
    DEFAULT_ANIMATION_CONFIG,
} from './types';
import { getImageUrl } from './utils/extractBackgroundImage';

/**
 * Generate a unique ID for tracking images
 */
function generateId(): string {
    return `lmp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Parse data-live-* and data-beautifi-* attributes from an element
 */
function parseDataAttributes(element: HTMLElement): AnimationConfig {
    const enabledAttr = element.dataset.beautifi ?? element.dataset.beautifai;
    const enabled = element.dataset.live !== 'false' && enabledAttr !== 'false';

    // Support both data-live-intensity and data-beautifi-intensity
    const intensityAttr =
        element.dataset.liveIntensity ||
        element.dataset.beautifiIntensity ||
        element.dataset.beautifaiIntensity;
    const intensity: AnimationIntensity =
        intensityAttr === 'subtle' || intensityAttr === 'medium' || intensityAttr === 'strong'
            ? intensityAttr
            : DEFAULT_ANIMATION_CONFIG.intensity;

    const typeAttr =
        element.dataset.liveType ||
        element.dataset.beautifiType ||
        element.dataset.beautifaiType;
    const type: AnimationType =
        typeAttr === 'breathing' || typeAttr === 'parallax' || typeAttr === 'sway' || typeAttr === 'auto'
            ? typeAttr
            : DEFAULT_ANIMATION_CONFIG.type;

    const modeAttr =
        element.dataset.liveMode ||
        element.dataset.beautifiMode ||
        element.dataset.beautifaiMode;
    const mode: AnimationMode | undefined =
        modeAttr === 'auto' || modeAttr === 'css' || modeAttr === 'video'
            ? modeAttr
            : undefined;

    const loopAttr = element.dataset.beautifiLoop ?? element.dataset.beautifaiLoop;
    const delayAttr =
        element.dataset.liveDelay ||
        element.dataset.beautifiDelay ||
        element.dataset.beautifaiDelay;

    const loop = element.dataset.liveLoop !== 'false' && loopAttr !== 'false';
    const delay = parseInt(delayAttr || '0', 10) || 0;

    return {
        enabled,
        mode,
        intensity,
        type,
        loop,
        delay,
    };
}

/**
 * ImageDetector class
 * 
 * Detects images in the DOM and parses their animation configuration.
 */
export class ImageDetector {
    private selector: string;
    private trackedImages: Map<string, TrackedImage> = new Map();
    private processedElements: WeakSet<HTMLElement> = new WeakSet();
    private mutationObserver: MutationObserver | null = null;
    private onImageDetected: ((image: TrackedImage) => void) | null = null;
    private debug: boolean;
    private optIn: boolean;

    constructor(selector: string = 'img', debug: boolean = false, optIn: boolean = false) {
        this.selector = selector;
        this.debug = debug;
        this.optIn = optIn;
    }

    /**
     * Set callback for when new images are detected
     */
    setOnImageDetected(callback: (image: TrackedImage) => void): void {
        this.onImageDetected = callback;
    }

    /**
     * Scan the DOM for images matching the selector
     */
    scan(): TrackedImage[] {
        const elements = document.querySelectorAll<HTMLElement>(this.selector);
        const newImages: TrackedImage[] = [];

        elements.forEach((element) => {
            if (this.processedElements.has(element)) {
                return;
            }

            // Get image URL from src attribute or background-image
            const imageInfo = getImageUrl(element);
            if (!imageInfo) {
                // No image found, skip this element
                this.processedElements.add(element);
                return;
            }

            const config = parseDataAttributes(element);

            // In opt-in mode, images must be explicitly enabled with data-beautifi-animate
            if (this.optIn) {
                const hasOptInAttr =
                    element.hasAttribute('data-beautifi-animate') ||
                    element.hasAttribute('data-beautifi') ||
                    element.hasAttribute('data-beautifai');

                if (!hasOptInAttr) {
                    this.processedElements.add(element);
                    if (this.debug) {
                        console.log('[beautifi] Skipping (opt-in mode, no data-beautifi-animate):', imageInfo.url);
                    }
                    return;
                }
            }

            // Skip if explicitly disabled
            if (!config.enabled) {
                this.processedElements.add(element);
                return;
            }

            const trackedImage: TrackedImage = {
                element,
                id: generateId(),
                state: 'idle',
                config,
                sourceType: imageInfo.sourceType,
                imageUrl: imageInfo.url,
            };

            this.trackedImages.set(trackedImage.id, trackedImage);
            this.processedElements.add(element);
            newImages.push(trackedImage);

            if (this.debug) {
                console.log('[beautifi] Detected image:', imageInfo.url, `(${imageInfo.sourceType})`, config);
            }
        });

        return newImages;
    }

    /**
     * Start observing the DOM for dynamically added images
     */
    observe(): void {
        if (this.mutationObserver) {
            return;
        }

        this.mutationObserver = new MutationObserver((mutations) => {
            let shouldScan = false;

            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    for (const node of mutation.addedNodes) {
                        if (
                            node instanceof HTMLImageElement ||
                            (node instanceof Element && node.querySelector(this.selector))
                        ) {
                            shouldScan = true;
                            break;
                        }
                    }
                }
                if (shouldScan) break;
            }

            if (shouldScan) {
                const newImages = this.scan();
                newImages.forEach((image) => {
                    if (this.onImageDetected) {
                        this.onImageDetected(image);
                    }
                });
            }
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    /**
     * Get all tracked images
     */
    getTrackedImages(): TrackedImage[] {
        return Array.from(this.trackedImages.values());
    }

    /**
     * Get a tracked image by ID
     */
    getTrackedImage(id: string): TrackedImage | undefined {
        return this.trackedImages.get(id);
    }

    /**
     * Update a tracked image's state
     */
    updateImageState(id: string, updates: Partial<TrackedImage>): void {
        const image = this.trackedImages.get(id);
        if (image) {
            Object.assign(image, updates);
        }
    }

    /**
     * Clean up and stop observing
     */
    destroy(): void {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        this.trackedImages.clear();
        this.onImageDetected = null;
    }
}
